import { Facebook, Instagram, Twitter, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import type { ElementType } from "react";
import logo from '../assets/logokalpa.png';
import { useLanguage } from "../context/LanguageContext";
const SafeIcon = ({ icon: Icon, className }: { icon?: ElementType; className?: string }) =>
  Icon ? <Icon className={className} /> : null;
export function Footer() {
  const { t } = useLanguage();
  // 0: Farm Development, 1: Precision Agriculture, 2: Irrigation Solutions, 3: Soil Testing, 4: Digital Farming, 5: Equipment Supply
  return (
    <footer className="bg-gradient-to-b from-[#051F20] to-[#0B2B26] text-white relative overflow-hidden">
      {/* Premium Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 50px 50px, #8EB69B 2%, transparent 0%)`,
            backgroundSize: '100px 100px'
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Main Footer */}
        <div className="px-6 lg:px-20 py-20">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info with Logo */}
            <div>
              <div 
                className="flex items-center gap-4 mb-6"
              >
                <div
                  className="relative"
                >
                  <div
                    className="absolute -inset-2 bg-gradient-to-r from-[#C9A961]/30 to-[#8EB69B]/30 rounded-2xl blur-xl"
                  />
                  <img 
                    src={logo} 
                    alt="Kalpakrushi" 
                    className="h-16 w-16 relative z-10 rounded-full object-cover border-2 border-[#C9A961]/30"
                    loading="lazy"
                  />
                </div>
                <div>
                  <span className="text-white text-2xl tracking-tight block">Kalpakrushi</span>
                  <span className="text-[#C9A961] text-xs uppercase tracking-[0.2em]">AgriTech Solutions</span>
                </div>
              </div>
              <p className="text-[#DAF1DE]/80 mb-6 leading-relaxed">
                Transforming Indian agriculture with smart technology and sustainable farming solutions.
              </p>
              <div className="flex gap-3">
                <a 
                  href="https://www.facebook.com/share/17fwUVMCJt/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-12 w-12 rounded-xl bg-[#235347]/30 backdrop-blur-md flex items-center justify-center hover:bg-gradient-to-br hover:from-[#8EB69B] hover:to-[#C9A961] border border-[#8EB69B]/20 transition-all duration-300"
                >
                  <SafeIcon icon={Facebook} className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.instagram.com/kalpakrushi?igsh=M283ZXRkZ241Z2o=" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-12 w-12 rounded-xl bg-[#235347]/30 backdrop-blur-md flex items-center justify-center hover:bg-gradient-to-br hover:from-[#8EB69B] hover:to-[#C9A961] border border-[#8EB69B]/20 transition-all duration-300"
                >
                  <SafeIcon icon={Instagram} className="h-5 w-5" />
                </a>
                <a 
                  href="#" 
                  className="h-12 w-12 rounded-xl bg-[#235347]/30 backdrop-blur-md flex items-center justify-center hover:bg-gradient-to-br hover:from-[#8EB69B] hover:to-[#C9A961] border border-[#8EB69B]/20 transition-all duration-300"
                >
                  <SafeIcon icon={Twitter} className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.linkedin.com/company/kalpakrushi/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-12 w-12 rounded-xl bg-[#235347]/30 backdrop-blur-md flex items-center justify-center hover:bg-gradient-to-br hover:from-[#8EB69B] hover:to-[#C9A961] border border-[#8EB69B]/20 transition-all duration-300"
                >
                  <SafeIcon icon={Linkedin} className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white text-xl mb-6 border-b border-[#8EB69B]/20 pb-3">{t('footer.quickLinks')}</h3>
              <ul className="space-y-3">
                {[
                  { label: 'Home', href: '#home' },
                  { label: 'About Us', href: '#about' },
                  { label: 'Our Services', href: '#services' },
                  { label: 'Projects', href: '#projects' },
                  { label: 'Digital App', href: '#digital-app' },
                  { label: 'Contact Us', href: '#contact' }
                ].map((link, idx) => (
                  <li 
                    key={idx}
                  >
                    <a href={link.href} className="text-[#DAF1DE]/80 hover:text-[#C9A961] transition-colors flex items-center gap-2 group">
                      <span 
                        className="h-1.5 w-1.5 bg-[#8EB69B] rounded-full"
                      />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-white text-xl mb-6 border-b border-[#8EB69B]/20 pb-3">Our Services</h3>
              <ul className="space-y-3">
                {[
                  { label: 'Farm Development', href: '#farm-development' },
                  { label: 'Precision Agriculture', href: '#precision-agriculture' },
                  { label: 'Irrigation Solutions', href: '#irrigation-solutions' },
                  { label: 'Soil Testing', href: '#soil-testing' },
                  { label: 'Digital Farming', href: '#digital-farming' },
                  { label: 'Equipment Supply', href: '#equipment-supply' }
                ].map((service, idx) => (
                  <li 
                    key={idx}
                  >
                    <a
                      href={service.href}
                      onClick={(e) => {
                        e.preventDefault();
                        const id = service.href.replace('#', '');
                        // update hash (this also triggers native scrolling)
                        window.location.hash = id;
                        // tell ServicesGrid to reveal/highlight the requested service
                        window.dispatchEvent(new CustomEvent('openService', { detail: { id, index: idx } }));
                      }}
                      className="text-[#DAF1DE]/80 hover:text-[#C9A961] transition-colors flex items-center gap-2 group"
                    >
                      <span 
                        className="h-1.5 w-1.5 bg-[#8EB69B] rounded-full"
                      />
                      {service.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="min-w-[302px]">
              <h3 className="text-white text-xl mb-6 border-b border-[#8EB69B]/20 pb-3">Contact Info</h3>
              <ul className="space-y-5">
                <li 
                  className="flex items-start gap-4"
                >
                  <div className="h-12 w-12 bg-gradient-to-br from-[#235347] to-[#8EB69B] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <SafeIcon icon={MapPin} className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-[#DAF1DE]/90">Sharada Nagar</p>
                    <p className="text-[#DAF1DE]/70 text-sm">Tiptur, Karnataka</p>
                  </div>
                </li>
                <li 
                  className="flex items-center gap-4"
                >
                  <div className="h-12 w-12 bg-gradient-to-br from-[#8EB69B] to-[#C9A961] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <SafeIcon icon={Phone} className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-[#DAF1DE]/90">8147851696</p>
                    <p className="text-[#DAF1DE]/90">8088841428</p>
                  </div>
                </li>
                <li 
                  className="flex items-center gap-4"
                >
                  <div className="h-12 w-12 bg-gradient-to-br from-[#C9A961] to-[#235347] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <SafeIcon icon={Mail} className="h-5 w-5 text-white" />
                  </div>
                  <div className="px-2">
                    <p className="text-[#DAF1DE]/90">info@kalpakrushi.com</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        {/* <div className="border-t border-[#8EB69B]/20 px-6 lg:px-20 py-8 backdrop-blur-sm bg-[#051F20]/50">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#DAF1DE]/70">
              © 2025 Kalpakrushi AgriTech. All rights reserved.
            </p>
            <div className="flex gap-8">
              {['Privacy Policy', 'Terms of Service', 'Sitemap'].map((link, idx) => (
                <motion.a 
                  key={idx}
                  href="#" 
                  className="text-[#DAF1DE]/70 hover:text-[#C9A961] transition-colors text-sm"
                  whileHover={{ y: -2 }}
                >
                  {link}
                </motion.a>
              ))}
            </div>
          </div>
        </div> */}

<div className="border-t border-[#8EB69B]/20 px-6 lg:px-20 py-8 backdrop-blur-sm bg-[#051F20]/50">
  <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
    <p className="text-[#DAF1DE]/70">
      © 2025 Kalpakrushi AgriTech. All rights reserved.
    </p>

    <div className="flex gap-8">
      {/* About Us */}
      <a
        href="#about-us"
        className="text-[#DAF1DE]/70 hover:text-[#C9A961] transition-colors text-sm"
      >
        About Us
      </a>

      {/* Privacy Policy */}
      <a
        href="#privacy-policy"
        className="text-[#DAF1DE]/70 hover:text-[#C9A961] transition-colors text-sm"
      >
        Privacy Policy
      </a>

      {/* Terms */}
      <a
        href="#"
        className="text-[#DAF1DE]/70 hover:text-[#C9A961] transition-colors text-sm"
      >
        Terms of Service
      </a>

      {/* Sitemap */}
      <a
        href="#"
        className="text-[#DAF1DE]/70 hover:text-[#C9A961] transition-colors text-sm"
      >
        Sitemap
      </a>
    </div>
  </div>
</div>


      </div>
    </footer>
  );
}